import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { Alert, Card, CardContent, TextInput } from '@guildeducationinc/recess';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav';
import { SAVE_FIELD_OF_STUDY } from '@src/graphql/queries/fieldOfStudy';
import { useFieldsOfStudy } from '@src/hooks/useFieldsOfStudy';
import { prepareForSave } from '@src/utilities/gqlHelper';
import { pick, sortBy } from 'lodash';
import { DataTable, Form, getLanguageDisplayName, SearchFilterBar, TrackableButton, useAlert, useContentItems, useLocale, useSessionState, } from 'pe-core-frontend';
import { useEffect, useMemo, useState } from 'react';
export const FieldsOfStudy = () => {
    const { t } = useLocale();
    const { showAlert } = useAlert();
    const [searchText, setSearchText] = useSessionState('fieldsOfStudySearch', '');
    const { fieldsOfStudy, loading, error, page, pageSize, setPage, setPageSize, total, orderBy, orderByDir, setOrderBy, setOrderByDir, refetch, } = useFieldsOfStudy({
        searchText,
    });
    const [saveFieldOfStudy] = useMutation(SAVE_FIELD_OF_STUDY);
    const [selectedFieldOfStudy, setSelectedFieldOfStudy] = useState(null);
    const contentItemsData = useContentItems({
        clientId: selectedFieldOfStudy?.content?.clientId,
        code: selectedFieldOfStudy?.content?.code,
    });
    const [contentItems, setContentItems] = useState(contentItemsData?.items || []);
    useEffect(() => {
        if (!selectedFieldOfStudy)
            return;
        setContentItems(contentItemsData.items || []);
    }, [selectedFieldOfStudy, contentItemsData.items]);
    const formFields = useMemo(() => {
        return [
            {
                name: 'category',
                label: t('Category'),
                disabled: true,
            },
            {
                name: 'name',
                label: t('Name'),
                disabled: true,
            },
            {
                name: 'isActive',
                label: t('Active?'),
                type: 'boolean',
                serializeValue: (value) => (value ? 'true' : 'false'),
                deserializeValue: (value) => value === 'true',
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleValidate = (fos) => {
        const errors = {};
        if (!fos.category) {
            errors.category = t('{field} is required', { values: { field: t('Category') } });
        }
        if (!fos.name) {
            errors.name = t('{field} is required', { values: { field: t('Name') } });
        }
        return errors;
    };
    const handleSave = async (fos) => {
        try {
            const result = await saveFieldOfStudy({
                variables: {
                    input: {
                        ...pick(prepareForSave(fos), 'category', 'name', 'isActive'),
                        id: fos.id ? fos.id : undefined,
                        contentItems: contentItems.map(ci => ({
                            ...prepareForSave(ci),
                        })),
                    },
                },
            });
            if (result.data?.saveFieldOfStudy && !result.data.saveFieldOfStudy.success) {
                return result.data.saveFieldOfStudy;
            }
            await refetch();
            setSelectedFieldOfStudy(null);
            const message = result.data.saveFieldOfStudy.messages[0];
            const toShow = t(message.value, { values: message.params });
            showAlert(toShow, 'success');
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            throw new Error(t('Unable to save field of study'));
        }
    };
    const handleSortModelChange = (sortModel) => {
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            setOrderBy(sort.field);
            setOrderByDir(sort.sort);
        }
        else {
            setOrderBy(null);
            setOrderByDir(null);
        }
    };
    const handleContentItemChange = (contentItem) => (e) => {
        setContentItems(prev => {
            const newContentItems = prev.map(ci => {
                if (ci.language === contentItem.language) {
                    return {
                        ...ci,
                        value: e.target.value,
                    };
                }
                return ci;
            });
            return newContentItems;
        });
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('Fields of study'), searchFilterActions: _jsx(SearchFilterBar, { enableSearch: true, searchText: searchText, onSearchChange: setSearchText, searchPlaceholder: t('Search by Name or Category') }) }), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), _jsx(DataTable, { disableRowSelectionOnClick: true, disableColumnMenu: true, rows: fieldsOfStudy, loading: loading, pageSizeOptions: [10, 25], paginationMode: 'server', paginationModel: { page: page - 1, pageSize }, onPaginationModelChange: pagination => {
                                    setPage(pagination.page + 1);
                                    setPageSize(pagination.pageSize);
                                }, onSortModelChange: handleSortModelChange, sortModel: [{ field: orderBy ?? 'name', sort: orderByDir }], rowCount: total, columns: [
                                    {
                                        field: 'name',
                                        headerName: t('Name'),
                                        flex: 0.4,
                                    },
                                    {
                                        field: 'category',
                                        headerName: t('Category'),
                                        flex: 0.3,
                                    },
                                    {
                                        field: 'isActive',
                                        headerName: t('Active?'),
                                        width: 100,
                                        renderCell: params => (params.value ? t('Yes') : t('No')),
                                    },
                                    {
                                        field: 'actions',
                                        headerName: t('Actions'),
                                        width: 100,
                                        renderCell: ({ row }) => (_jsx("div", { children: _jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `field_of_study_${row.id}`, appearance: 'minimal', label: t('Edit'), icon: _jsx(EditIcon, { size: 20 }), onClick: () => {
                                                    setSelectedFieldOfStudy(row);
                                                } }) })),
                                    },
                                ] })] }) }) }), selectedFieldOfStudy && (_jsx(Form, { fields: formFields, title: 'Edit field of study', modal: true, obj: selectedFieldOfStudy, onValidate: handleValidate, onSave: handleSave, minHeight: 150, onCancel: () => setSelectedFieldOfStudy(null), children: () => (_jsx("div", { className: 'flex flex-col gap-3', children: sortBy(contentItems, it => getLanguageDisplayName(it.language)).map(contentItem => (_jsxs("div", { children: [_jsx("h4", { className: 'text-2xl', children: getLanguageDisplayName(contentItem.language) }), _jsx(TextInput, { label: t('Name'), hideLabel: true, className: 'max-w-full', value: contentItem.value || '', onChange: handleContentItemChange(contentItem) })] }, contentItem.language))) })) }))] }));
};
